import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { useAuth } from "@/_core/hooks/useAuth";
import { useEffect, useRef } from "react";
import { useLocation } from "wouter";

// Map paths to section names for tracking
const SECTION_MAP: Record<string, string> = {
  "/sales": "sales",
  "/command": "command",
};

/**
 * Hook that provides notification counts and auto-marks sections as visited
 */
export function useNotifications() {
  const { user } = useAuth();
  const [location] = useLocation();
  const prevLocation = useRef(location);

  // Convex reactive query — sem polling, atualiza sozinho via WebSocket
  const counts = useQuery(api.notifications.counts, user ? {} : "skip");

  const markVisited = useMutation(api.notifications.markVisited);

  // Auto-mark section as visited when user navigates to it
  useEffect(() => {
    const section = SECTION_MAP[location];
    if (section && user) {
      // Small delay to ensure the page has loaded
      const timer = setTimeout(() => {
        (async () => {
          try {
            await markVisited({ section });
          } catch (err) {
            console.error("[NotificationBadge] markVisited failed", err);
          }
        })();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location, user, markVisited]);

  // Track location changes for badge animation
  useEffect(() => {
    prevLocation.current = location;
  }, [location]);

  return {
    counts: counts || { sales: 0, command: 0 },
    isLoading: counts === undefined,
  };
}

/**
 * Red notification badge component (Instagram-style)
 */
export function NotificationBadge({ count }: { count: number }) {
  if (count <= 0) return null;

  const displayCount = count > 99 ? "99+" : count.toString();

  return (
    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-lg shadow-red-500/30 animate-in zoom-in-50 duration-200 z-10">
      {displayCount}
    </span>
  );
}
